"use client";

import textconfig from "@/textconfig";
import { ProductCardType } from "@/types/product";
import { atom, useAtom, useSetAtom } from "jotai";
import { Suspense, useEffect } from "react";
import styles from "./SearchTabs.module.css";

export type SearchTabsProps = {
	results: number;
	cardRecords?: Record<
		string,
		Omit<ProductCardType, "relatedProducts" | "variants">[]
	>;
};

const DEFAULT = "all";

export const activeTabAtom = atom<string>(DEFAULT);

const UnsuspendedSearchTabs = ({ results, cardRecords }: SearchTabsProps) => {
	const setActiveTab = useSetAtom(activeTabAtom);

	const categories = Object.keys(cardRecords ?? {});

	const hideAllTab = categories.length === 1;

	useEffect(() => {
		if (hideAllTab) {
			setActiveTab(categories[0]);
		}
	}, [categories, hideAllTab, setActiveTab]);

	useEffect(() => {
		if (!hideAllTab) {
			setActiveTab(DEFAULT);
		}
	}, [hideAllTab, setActiveTab]);

	if (!cardRecords) return null;

	return (
		<nav className={styles.root}>
			{!hideAllTab && <Tab value={DEFAULT} cardsLength={results} />}
			{categories.map((category) => {
				const cards = cardRecords[category];
				return (
					<Tab
						key={"category--" + category}
						value={category}
						cardsLength={cards.length}
					/>
				);
			})}
		</nav>
	);
};

const displayTabs = textconfig.display.tabs;

const Tab = ({
	value,
	cardsLength,
}: {
	value: string;
	cardsLength: number;
}) => {
	const [activeTab, setActiveTab] = useAtom(activeTabAtom);
	const tagText = displayTabs[value as keyof typeof displayTabs] ?? value;
	return (
		<button
			className={styles.tag}
			disabled={activeTab === value}
			data-card-type={value}
			onClick={() => setActiveTab(value)}
		>{`${tagText}`}</button>
	);
};

export const SearchTabs: typeof UnsuspendedSearchTabs = (props) => (
	<Suspense
		fallback={
			<div>
				<button
					className={styles.tag}
					key={"fallback--key--" + "Alle"}
				>{`${"Alle"})`}</button>
			</div>
		}
	>
		<UnsuspendedSearchTabs {...props} />
	</Suspense>
);
